import { EOrganization, ESnapshotExists } from 'lib/types';
import { OrganizationStatus, State } from 'lib/enums';
import { ShippingTier } from 'lib/types/mail';
import {
  DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS,
  DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS,
  DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS,
  DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS,
  DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS,
  NoticeAddOnPricingConfig
} from 'lib/pricing/noticeAddOns/types';

/**
 * Publisher (newspaper) organization ids that never offer the wet-signature
 * affidavit option — their customers only get the digital (RON) affidavit with
 * an email/mail delivery choice. Matched against the notice's publisher (or its
 * parent) so it applies for any placer, publisher or advertiser.
 */
export const WET_SIG_DISABLED_PUBLISHER_ORG_IDS: string[] = [
  // Ogden parent org
  'pEdqCNuQy0iDCPpJ05IJ',
  // Swift Communications parent org
  'Uo1IKa1wC1H4HKLXqtmH',
  // Lee Enterprises parent org
  'FF0H4A8r7oZgNQI1s3uD',
  // Lee Enterprises parent org
  'HwTxiaAmR8ERWEdUnoR1',
  // Hoffmain Media Group parent org
  'SI6g7vnQJT7yZDnOeWej'
];

/**
 * Publisher (newspaper) organization ids that never offer *additional* paid
 * wet-signature affidavits. Their wet-signature papers mail the included copy
 * and stop there, so the customer is never quoted a per-affidavit cost.
 *
 * Separate from the list above because the two answer different questions: this
 * one only matters once a notice already requires a wet signature.
 */
export const WET_SIG_UPSELL_DISABLED_PUBLISHER_ORG_IDS: string[] = [
  // Lee Enterprises parent org
  'FF0H4A8r7oZgNQI1s3uD',
  // Lee Enterprises parent org
  'HwTxiaAmR8ERWEdUnoR1',
  // Hoffmain Media Group parent org
  'SI6g7vnQJT7yZDnOeWej'
];

/**
 * Publisher (newspaper) organization ids that never mail anything the customer
 * pays for — no mailed affidavit copy, no mailed invoice, no wet-signature
 * shipping. Lee and Hoffman bill their own advertisers, so a mailing Column
 * fronts the postage for cannot be recovered from them.
 *
 * Separate from the wet-sig lists above because it answers a third question:
 * those turn off an affidavit *option*, this turns off *delivery by post*.
 */
export const MAILED_DOCS_DISABLED_PUBLISHER_ORG_IDS: string[] = [
  // Lee Enterprises parent org
  'FF0H4A8r7oZgNQI1s3uD',
  // Lee Enterprises parent org
  'HwTxiaAmR8ERWEdUnoR1',
  // Hoffman Media Group parent org
  'SI6g7vnQJT7yZDnOeWej'
];

/** Matches a notice's publisher, or the parent it belongs to, against a list. */
function publisherOrParentIsIn(
  publisherOrganization: ESnapshotExists<EOrganization> | undefined,
  orgIds: string[]
): boolean {
  if (!publisherOrganization) {
    return false;
  }
  const parentId = publisherOrganization.data().parent?.id;
  return (
    orgIds.includes(publisherOrganization.id) ||
    (!!parentId && orgIds.includes(parentId))
  );
}

/**
 * Whether the notice's publisher (or its parent) is configured to never offer
 * the wet-signature affidavit option.
 */
export function isWetSignatureDisabledForPublisher(
  publisherOrganization: ESnapshotExists<EOrganization> | undefined
): boolean {
  return publisherOrParentIsIn(
    publisherOrganization,
    WET_SIG_DISABLED_PUBLISHER_ORG_IDS
  );
}

/**
 * Whether the notice's publisher (or its parent) is configured to never sell
 * additional wet-signature affidavits on top of the included one.
 */
export function isWetSigUpsellDisabledForPublisher(
  publisherOrganization: ESnapshotExists<EOrganization> | undefined
): boolean {
  return publisherOrParentIsIn(
    publisherOrganization,
    WET_SIG_UPSELL_DISABLED_PUBLISHER_ORG_IDS
  );
}

/**
 * States where no paper mails a customer-paid document, by state law or policy.
 */
export const MAILED_DOCS_DISABLED_STATES: number[] = [
  State.wisconsin.value,
  State.florida.value,
  State.mississippi.value,
  State.south_carolina.value
];

/**
 * Whether the notice's publisher mails no customer-paid documents at all —
 * because its state never has, or because it individually opted out. This is
 * the frontend gate only — pricing still bills any mail record present.
 */
export function areMailedDocsDisabledForPublisher(
  publisherOrganization: ESnapshotExists<EOrganization> | undefined
): boolean {
  const state = publisherOrganization?.data().state;
  return (
    (state !== undefined && MAILED_DOCS_DISABLED_STATES.includes(state)) ||
    publisherOrParentIsIn(
      publisherOrganization,
      MAILED_DOCS_DISABLED_PUBLISHER_ORG_IDS
    )
  );
}

export enum PaperType {
  NON_AA = 'non_aa',
  /**
   * AA papers with no wet sig requirement.
   */
  WET_SIG_NOT_REQUIRED = 'wet_sig_not_required',
  WET_SIG_REQUIRED = 'wet_sig_required',
  WET_SIG_REQUIRED_NO_MAILING = 'wet_sig_required_no_mailing',
  EXTENDED = 'extended',
  GANNETT = 'gannett'
}

export type AffidavitType = 'ron' | 'wet_sig' | 'digital';

export type ShippingSpeed = 'ground' | 'express' | 'rush';

export const SHIPPING_SPEED_LABELS: Record<ShippingSpeed, string> = {
  ground: 'Ground (3–5 business days)',
  express: 'Express (2 business days)',
  rush: 'Rush (1 business day)'
};

export function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/**
 * Maps a ShippingSpeed (UI label key) to the canonical ShippingTier enum value.
 */
export function shippingSpeedToTier(speed: ShippingSpeed): ShippingTier {
  const map: Record<ShippingSpeed, ShippingTier> = {
    ground: ShippingTier.GROUND,
    express: ShippingTier.EXPRESS,
    rush: ShippingTier.RUSH
  };
  return map[speed];
}

/**
 * Maps a ShippingTier back to the UI ShippingSpeed key.
 */
export function shippingTierToSpeed(tier: ShippingTier): ShippingSpeed {
  const map: Record<ShippingTier, ShippingSpeed> = {
    [ShippingTier.GROUND]: 'ground',
    [ShippingTier.EXPRESS]: 'express',
    [ShippingTier.RUSH]: 'rush'
  };
  return map[tier];
}

/**
 * Returns the unit price in cents for a given shipping speed, using the org
 * config with fallbacks to system defaults.
 */
export function getShippingPriceCents(
  speed: ShippingSpeed,
  config: NoticeAddOnPricingConfig
): number {
  switch (speed) {
    case 'ground':
      return (
        config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS
      );
    case 'express':
      return (
        config.expressShippingFeeInCents ??
        DEFAULT_EXPRESS_SHIPPING_FEE_IN_CENTS
      );
    case 'rush':
      return (
        config.rushShippingFeeInCents ?? DEFAULT_RUSH_SHIPPING_FEE_IN_CENTS
      );
  }
}

/**
 * Returns the wet-sig affidavit fee per copy in cents from org config.
 */
export function getWetSigFeeCents(config: NoticeAddOnPricingConfig): number {
  return (
    config.wetSigAffidavitFeeInCents ?? DEFAULT_WET_SIG_AFFIDAVIT_FEE_IN_CENTS
  );
}

/**
 * Returns the RON affidavit fee in cents from org config.
 */
export function getRonFeeCents(config: NoticeAddOnPricingConfig): number {
  return config.ronAffidavitFeeInCents ?? DEFAULT_RON_AFFIDAVIT_FEE_IN_CENTS;
}

/**
 * Returns the ground shipping fee per copy (for Lob-mailed RON/invoice/digital).
 */
export function getGroundShippingFeeCents(
  config: NoticeAddOnPricingConfig
): number {
  return (
    config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS
  );
}

type DerivePaperTypeParams = {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  wetSigRequired: boolean;
  /**
   * Whether Column manages affidavits for *this* notice, resolved through the
   * full newspaper -> notice type -> customer org -> customer cascade (see
   * `getAffidavitSettingsForNoticeData`). Must not be read off the publisher
   * org alone: a paper can be in the AA program while having AA disabled for an
   * individual customer or notice type, and those notices are non-AA.
   */
  affidavitsManagedByColumn: boolean;
};

/**
 * Derives the paper type from real org data. Replaces the hardcoded dev
 * fixtures with real runtime resolution.
 */
export function derivePaperType({
  publisherOrganization,
  wetSigRequired,
  affidavitsManagedByColumn
}: DerivePaperTypeParams): PaperType {
  if (!publisherOrganization) {
    return PaperType.NON_AA;
  }

  const orgData = publisherOrganization.data();
  const isExtendedNetwork =
    orgData.organizationStatus === OrganizationStatus.express.value;
  const isAdjudicated = (orgData.adjudicationAreas?.length ?? 0) > 0;

  if (isExtendedNetwork) {
    return isAdjudicated ? PaperType.GANNETT : PaperType.EXTENDED;
  }

  if (!affidavitsManagedByColumn) {
    return PaperType.NON_AA;
  }

  if (wetSigRequired) {
    // Some states never mail extras; some publishers (Lee) opt out of
    // selling them for the same reason — the customer should not meet a
    // per-affidavit charge they did not ask for.
    if (
      areMailedDocsDisabledForPublisher(publisherOrganization) ||
      isWetSigUpsellDisabledForPublisher(publisherOrganization)
    ) {
      return PaperType.WET_SIG_REQUIRED_NO_MAILING;
    }
    return PaperType.WET_SIG_REQUIRED;
  }

  return PaperType.WET_SIG_NOT_REQUIRED;
}

/**
 * Whether the publisher covers the first wet-signature affidavit (and its ground
 * shipment). True for wet-sig-required papers.
 */
export function isFirstWetSigFree(paperType: PaperType): boolean {
  return paperType === PaperType.WET_SIG_REQUIRED;
}

/**
 * Which affidavit options a paper type allows. `wetSigOffered` drops the
 * wet-signature choice on AA RON papers configured to never require one.
 */
export function allowedAffidavitTypes(
  paperType: PaperType,
  wetSigOffered = true
): AffidavitType[] {
  switch (paperType) {
    case PaperType.NON_AA:
    case PaperType.EXTENDED:
      return ['digital'];
    case PaperType.WET_SIG_NOT_REQUIRED:
    case PaperType.GANNETT:
      return wetSigOffered ? ['ron', 'wet_sig'] : ['ron'];
    case PaperType.WET_SIG_REQUIRED:
    case PaperType.WET_SIG_REQUIRED_NO_MAILING:
      return ['wet_sig'];
    default:
      return ['digital'];
  }
}

type ResolveAffidavitTypeParams = {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  wetSigRequired: boolean;
  affidavitsManagedByColumn: boolean;
  /** The value currently stored in state, which may be null on first render. */
  storedAffidavitType: AffidavitType | null;
  /** Whether the wet-signature option is offered on an AA RON paper. */
  wetSigOffered?: boolean;
};

/**
 * Resolves the effective affidavit type given org config and whatever type
 * the user has stored in state. Falls back to the first allowed type so callers
 * never have to handle null.
 */
export function resolveEffectiveAffidavitType({
  publisherOrganization,
  wetSigRequired,
  affidavitsManagedByColumn,
  storedAffidavitType,
  wetSigOffered = true
}: ResolveAffidavitTypeParams): AffidavitType {
  const paperType = derivePaperType({
    publisherOrganization,
    wetSigRequired,
    affidavitsManagedByColumn
  });
  const allowed = allowedAffidavitTypes(paperType, wetSigOffered);
  return storedAffidavitType && allowed.includes(storedAffidavitType)
    ? storedAffidavitType
    : allowed[0] ?? 'digital';
}
